import { Issue } from '../types';

/**
 * Constructs the Azure Blob Storage URL for the issues.json file
 * @param storageAccountName Azure Storage Account name
 * @param containerName Blob container name
 * @param blobName Name of the blob file
 * @param sasToken SAS token with query parameters
 * @returns Complete URL to the blob
 */
export const constructBlobUrl = (
  storageAccountName: string,
  containerName: string,
  blobName: string,
  sasToken: string
): string => {
  // Ensure SAS token starts with '?'
  const formattedSasToken = sasToken.startsWith('?') ? sasToken : `?${sasToken}`;
  
  return `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobName}?${formattedSasToken}`;
};

/**
 * Checks if Azure Blob Storage is configured via environment variables
 * @returns true if all required Azure environment variables are set
 * Note: SAS token is optional for public blob containers
 */
export const isAzureStorageConfigured = (): boolean => {
  return !!(
    import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME &&
    import.meta.env.VITE_AZURE_BLOB_CONTAINER_NAME &&
    import.meta.env.VITE_AZURE_BLOB_NAME
  );
};

/**
 * Fetches issues from Azure Blob Storage
 * @param retries Number of retry attempts (default: 3)
 * @param retryDelay Delay between retries in milliseconds (default: 1000)
 * @returns Promise resolving to an array of Issue objects
 * @throws Error if the fetch fails
 */
export const fetchIssuesFromBlob = async (
  retries: number = 3,
  retryDelay: number = 1000
): Promise<Issue[]> => {
  const storageAccountName = import.meta.env.VITE_AZURE_STORAGE_ACCOUNT_NAME;
  const containerName = import.meta.env.VITE_AZURE_BLOB_CONTAINER_NAME || 'trace';
  const blobName = import.meta.env.VITE_AZURE_BLOB_NAME || 'trace-0_3.json';
  const sasToken = import.meta.env.VITE_AZURE_SAS_TOKEN || ''; // Optional for public blobs

  if (!storageAccountName) {
    throw new Error('Azure Storage configuration is incomplete. VITE_AZURE_STORAGE_ACCOUNT_NAME is required.');
  }

  // Construct URL - if no SAS token, use public blob URL
  const blobUrl = sasToken 
    ? constructBlobUrl(storageAccountName, containerName, blobName, sasToken)
    : `https://${storageAccountName}.blob.core.windows.net/${containerName}/${blobName}`;
  
  // Log the URL for debugging (without exposing the full SAS token)
  const urlForLogging = sasToken ? blobUrl.split('?')[0] + '?[SAS_TOKEN]' : blobUrl;
  console.log(`Fetching blob from: ${urlForLogging}`);
  console.log(`Storage Account: ${storageAccountName}, Container: ${containerName}, Blob: ${blobName}`);

  let lastError: Error | null = null;

  // Retry logic
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      // Log CORS preflight info
      if (attempt === 0) {
        console.log('Origin:', window.location.origin);
        console.log('Full blob URL (first 100 chars):', blobUrl.substring(0, 100) + '...');
      }

      const response = await fetch(blobUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        signal: AbortSignal.timeout(30000), // 30 second timeout
      });

      if (!response.ok) {
        // Handle specific HTTP error codes
        if (response.status === 403) {
          throw new Error('Access denied (403). Check that your SAS token is valid and has Read permissions.');
        } else if (response.status === 404) {
          throw new Error(`Blob not found (404). Verify that "${blobName}" exists in container "${containerName}".`);
        } else if (response.status === 401) {
          throw new Error('Authentication failed (401). Your SAS token may be expired or invalid.');
        } else {
          throw new Error(`Failed to fetch from Azure Blob Storage: ${response.status} ${response.statusText}`);
        }
      }

      const data: Issue[] = await response.json();
      
      // Validate that we got an array
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format: Expected an array of issues.');
      }

      console.log(`Successfully loaded ${data.length} issues from Azure Blob Storage`);
      return data;
    } catch (error) {
      lastError = error as Error;

      // Don't retry on certain errors
      if (
        error instanceof Error &&
        (error.message.includes('403') || 
         error.message.includes('401') || 
         error.message.includes('404') ||
         error.message.includes('Invalid response format'))
      ) {
        throw error;
      }

      // If this isn't the last attempt, wait and retry
      if (attempt < retries) {
        console.warn(`Attempt ${attempt + 1} failed. Retrying in ${retryDelay}ms...`, error);
        await new Promise(resolve => setTimeout(resolve, retryDelay));
        // Exponential backoff
        retryDelay *= 2;
      }
    }
  }

  // If we get here, all retries failed
  const errorMessage = lastError?.message || 'Unknown error occurred';
  throw new Error(`Failed to fetch issues from Azure Blob Storage after ${retries + 1} attempts: ${errorMessage}`);
};

